import type { GraphicsConfig } from '@sharefe/share3d-engine'

export type ExampleGraphicsBackend = 'webgl2' | 'webgpu'

const BACKENDS: readonly ExampleGraphicsBackend[] = ['webgl2', 'webgpu']

export interface ExampleGraphicsUrlConfig {
  /** URL 请求的后端；缺省为 webgl2。 */
  readonly requestedBackend: ExampleGraphicsBackend
  /** URL 是否显式写了 backend/renderer 参数。 */
  readonly explicit: boolean
}

export interface ExampleGraphicsUrlOptions {
  /** 该页面是否支持 WebGPU；不支持时请求值仍会被解析，由调用方决定降级。 */
  readonly webgpuSupported?: boolean
}

function parseHash(hash: string): URLSearchParams {
  return new URLSearchParams(hash.split('?')[1] ?? '')
}

function isBackend(value: string | null): value is ExampleGraphicsBackend {
  return value !== null && (BACKENDS as readonly string[]).includes(value)
}

/**
 * 读取统一的 graphics URL 配置。
 *
 * 兼容三种写法：
 * - `backend=webgl2|webgpu`（本引擎 example 的主参数）；
 * - `renderer=webgl2|webgpu`（PlayCanvas 对照页沿用过的历史写法）；
 * - 二者同时出现时以 `backend` 为准。
 */
export function readGraphicsUrlConfig(hash = window.location.hash): ExampleGraphicsUrlConfig {
  const query = parseHash(hash)
  const backendParam = query.get('backend')
  const rendererParam = query.get('renderer')
  const explicit = isBackend(backendParam) || isBackend(rendererParam)
  const requestedBackend = isBackend(backendParam)
    ? backendParam
    : isBackend(rendererParam)
      ? rendererParam
      : 'webgl2'
  return { requestedBackend, explicit }
}

/** 按设备支持情况解析实际后端；`webgpuSupported === false` 时回退 webgl2。 */
export function resolveGraphicsBackend(
  config: ExampleGraphicsUrlConfig,
  options: ExampleGraphicsUrlOptions = {}
): ExampleGraphicsBackend {
  if (config.requestedBackend === 'webgpu' && options.webgpuSupported === false) {
    return 'webgl2'
  }
  return config.requestedBackend
}

/** 读取显式 GPU timestamp-query 采集开关。 */
export function isGpuTimingRequested(hash = window.location.hash): boolean {
  const query = parseHash(hash)
  return query.get('gpuTiming') === '1' || query.get('gpuProfiling') === '1'
}

/** 生成传给 createPotreeEngine 的 graphics 配置。 */
export function toGraphicsConfig(
  backend: ExampleGraphicsBackend,
  options: { gpuProfiling?: boolean } = {}
): GraphicsConfig {
  return {
    backend,
    ...(backend === 'webgpu' && options.gpuProfiling === true ? { gpuProfiling: true } : {}),
  }
}

/** 把已解析的后端写回 hash，保留其它查询参数不变（用于规范化 URL，避免重载循环）。 */
export function writeGraphicsUrlBackend(hash: string, backend: ExampleGraphicsBackend): string {
  const [pathPart, queryPart = ''] = hash.split('?')
  const query = new URLSearchParams(queryPart)
  if (query.get('backend') === backend) {
    return hash
  }
  query.set('backend', backend)
  return `${pathPart}?${query.toString()}`
}

/** 当前浏览器是否具备 WebGPU 入口（存在即视为可用，实际失败由运行时结构化错误给出）。 */
export function isWebGPUAvailable(target: Navigator | undefined = globalThis.navigator): boolean {
  return Boolean(target && 'gpu' in target)
}
